import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Web } from '@mui/icons-material';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { getExpenseSearchOptions, getExpensesFiltered, getHotels, getStates } from './operations';
import SearchExpensesForm from './SearchExpensesForm';
import { reset, setForm } from './searchExpensesFormSlice';
import { buildStatementDateRange, getSearchExpensesCsvData } from './utils';
import Excel from '../../../../assets/excel.png';
import { Breadcrumbs, Button, SubHeader, ExpensesWindow, ResultsTable } from '../../../../components';
import ErrorModal from '../../../../components/ErrorModal';
import useAbortableQuery from '../../../../hooks/useAbortableQuery';
import { newDateFormatted } from '../../../../utils';
import ImageColorChanger from '../../../../utils/imageColorChanger';
import { getFormData as getFormAirlineData } from '../Airline/operations';
import { getCarRentals } from '../Car/operations';
import { getCitiesFiltered } from '../Hotel/operations';
import { getClientBranches } from '../operations';

import './styles.scoped.css';

const utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

// CP-859: hard ceiling on the paging drain. The widest search measured on dev needed 15 pages and
// on staging 28, so this is far above any legitimate result set - it exists purely so a backend
// that keeps handing back a lastEvaluatedKey cannot spin the browser forever.
const MAX_SEARCH_PAGES = 200;

const INCOMPLETE_SEARCH_MESSAGE = 'Your search stopped early, so these results are incomplete. Please try again.';

const SearchExpenses = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { clientMenu } = useSelector(state => state.expenseEntry);
  const { attachedOptions, form, statuses } = useSelector(state => state.searchExpensesForm);

  const [airlines, setAirlines] = useState([]);
  const [branches, setBranches] = useState([]);
  const [carRentals, setcarRentals] = useState([]);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [isLoadingAirlines, setIsLoadingAirlines] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingCarRentals, setIsLoadingCarRentals] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
  const [isLoadingHotels, setIsLoadingHotels] = useState(false);
  // CP-859: only the setter is used; the flag was read solely by a prop SearchExpensesForm
  // never destructured.
  const [, setIsLoadingStates] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [states, setStates] = useState([]);
  const [selectedCities, setSelectedCities] = useState();
  const [errors, setErrors] = useState();
  const [assignmentLineOfBusinessItems, setAssignmentLineOfBusinessItems] = useState({});
  // CP-859: a failed page, as opposed to a failed form field. `errors` above is form validation.
  const [searchError, setSearchError] = useState(null);

  // CP-859: whether the result set on screen is known to be partial. Deliberately SEPARATE from
  // searchError, which the user can dismiss: tying the export gate to the dismissible message
  // meant closing the warning re-enabled the export on the very result set the warning was about.
  // Only starting a new search, or cancelling, clears this.
  const [resultsIncomplete, setResultsIncomplete] = useState(false);

  // CP-859: results now append per page, so two overlapping drains would interleave into one
  // array. This guards each page against a newer search having started. Note it is a
  // monotonic-counter guard, not a real abort - the in-flight request still completes, it is
  // just ignored.
  //
  // The counter is per-request rather than per-search, which looks like it should misfire across
  // a multi-page drain. It cannot: the loop awaits each page before issuing the next, so only one
  // request is ever in flight and the counter only advances when cancel() runs or a new search
  // starts. Do not "fix" this into a per-search token without re-checking that invariant.
  const { runQuery, cancel } = useAbortableQuery();

  // CP-859: identifies the drain that currently owns the screen. runQuery's `aborted` flag only
  // covers the resolve path - a superseded drain whose in-flight page *rejects* bypasses it
  // entirely and lands in the shared catch/finally, where it would raise an error over, and clear
  // the spinner of, the search that replaced it. Every exit path is checked against this instead.
  const activeSearchRef = useRef(0);

  const getAssignmentLineOfBusinessItems = () => {
    clientMenu?.menuItems?.forEach(i => {
      if (location.pathname.includes(i.to)) {
        setAssignmentLineOfBusinessItems(i);
      }
    });
  };

  useLayoutEffect(() => {
    if (location.pathname.includes('driver-management-services')) {
      document.querySelector('div.root').style.background = '#D0CED0';
      document.querySelector('span.wrap').style.background = '#ffff';
      document.querySelector('.right-title').style.color = '#66CCFF';
      document.querySelector('.left-title').style.color = '#ffff';
    }
    return () => {
      document.querySelector('div.root').style.background = '#ffff';
    };
  }, []);

  // CP-813: Driver and Customer criteria come from expenses (incl. termed drivers), not the active-only
  // EDGE lists. getExpenseSearchOptions is cached per-lob, so the two calls share a single round trip.
  const getDriversToSelect = async () => {
    try {
      const options = await getExpenseSearchOptions(assignmentLineOfBusinessItems.lob);
      setDrivers(options?.drivers || []);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const getClientsToSelect = async () => {
    try {
      const options = await getExpenseSearchOptions(assignmentLineOfBusinessItems.lob);
      setClients(options?.customers || []);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const getHotelsToSelect = async () => {
    try {
      const newHotels = await getHotels();
      setHotels(newHotels);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const getStatesToSelect = async () => {
    try {
      const newStates = await getStates();
      setStates(newStates);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const getAirlineToSelect = async () => {
    try {
      const newAirlines = await getFormAirlineData();
      setAirlines(newAirlines?.airlines);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const getCarRentalsToSelect = async () => {
    try {
      const newCarRentals = await getCarRentals();
      setcarRentals(newCarRentals?.items);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const getBranchToSelect = async () => {
    try {
      const newBranches = await getClientBranches();
      setBranches(newBranches);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const handleGetData = async () => {
    // Drivers
    setIsLoadingDrivers(true);
    getDriversToSelect()
      .catch(error => console.log('error', error))
      .finally(() => setIsLoadingDrivers(false));

    // Customers
    setIsLoadingCustomers(true);
    getClientsToSelect()
      .catch(error => console.log('error', error))
      .finally(() => setIsLoadingCustomers(false));

    // Branches
    setIsLoadingBranches(true);
    getBranchToSelect()
      .catch(error => console.log('error', error))
      .finally(() => setIsLoadingBranches(false));

    // Airlines
    setIsLoadingAirlines(true);
    getAirlineToSelect()
      .catch(error => console.log('error', error))
      .finally(() => setIsLoadingAirlines(false));

    // Car Rentals
    setIsLoadingCarRentals(true);
    getCarRentalsToSelect()
      .catch(error => console.log('error', error))
      .finally(() => setIsLoadingCarRentals(false));

    // Hotels
    setIsLoadingHotels(true);
    getHotelsToSelect()
      .catch(error => console.log('error', error))
      .finally(() => {
        setIsLoadingHotels(false);
      });

    // States
    setIsLoadingStates(true);
    getStatesToSelect()
      .catch(error => console.log('error', error))
      .finally(() => {
        setIsLoadingStates(false);
      });
  };

  useEffect(() => {
    if (Object.keys(assignmentLineOfBusinessItems).length > 0) {
      handleGetData();
    }
  }, [assignmentLineOfBusinessItems]);

  useEffect(() => {
    getAssignmentLineOfBusinessItems();
  }, []);

  const setField = (fields, value) => {
    if (Array.isArray(fields)) {
      const itemsToAdd = {};
      const newErrors = { ...errors };
      fields.forEach(field => {
        itemsToAdd[field?.fieldName] = field?.fieldValue;
        if (errors && !!errors[field.fieldName]) delete newErrors[field?.fieldName];
      });
      dispatch(
        setForm({
          ...form,
          ...itemsToAdd,
        }),
      );
      setErrors({
        ...newErrors,
      });
    } else {
      dispatch(
        setForm({
          ...form,
          [fields]: value,
        }),
      );
      if (errors && !!errors[fields]) {
        setErrors({
          ...errors,
          [fields]: null,
        });
      }
    }
  };

  const fieldsAreValid = () => {
    let valid = true;
    const newErrors = {};

    const requiredExpenseFields = [
      'enteredStartDate',
      'enteredEndDate',
      'payWeekEndingStart',
      'payWeekEndingEnd',
      'receiptStartDate',
      'receiptEndDate',
      'statementStartDate',
      'statementEndDate',
      'lob',
    ];

    Object.keys(form).forEach(key => {
      requiredExpenseFields.forEach(rf => {
        if (rf === key) {
          const removedEnding = rf.replace('Ending', '__'); // Remove Ending word to prevent error removing End word
          let theOtherKey = removedEnding;
          if (removedEnding.includes('Start')) {
            // Replace Start with End and put Ending word back
            theOtherKey = removedEnding.replace('Start', 'End').replace('__', 'Ending');
          } else {
            // Replace End with Start and put Ending word back
            theOtherKey = removedEnding.replace('End', 'Start').replace('__', 'Ending');
          }
          if (
            (form[key] === '' || form[key] === undefined) &&
            form[theOtherKey] !== '' &&
            form[theOtherKey] !== undefined
          ) {
            newErrors[key] = 'Please provide a valid value.';
            valid = false;
          }
        }
      });
    });

    setErrors(newErrors);

    return valid;
  };

  const formatExpenses = exp => {
    if (exp) {
      const formattedExpenses = [...exp];
      formattedExpenses?.forEach(e => {
        if (e?.driverId && e?.driverName) {
          e.driverNameNumber = `${e?.driverName} - ${e?.driverId}`;
        }
        e.customerNameNumber = `${e?.customerName} - ${e?.customerId}`;
      });
      return formattedExpenses;
    }
    return [];
  };

  const formatFilters = () => {
    let fields = [];

    if (!showAdvancedSearch) {
      fields = ['status', 'code', 'customerId', 'driverId', 'lob'];
    } else {
      fields = [
        'status',
        'code',
        'costCenter',
        'branch',
        'customerId',
        'driverId',
        'notes',
        'assignmentId',
        'endDate',
        'createdAt',
        'payWeekEnding',
        'documentName',
        'expenseItemHotelId',
        'expenseItemHotelStateId',
        'expenseItemHotelCityId',
        'expenseItemAirLineId',
        'expenseItemCarRentalCompanyId',
        'isReceiptAttached',
        'lob',
        'creditCardHolderName',
        'expenseReportAppropriationNbr',
      ];
    }

    const filters = {};

    fields.forEach(f => {
      if (form?.[f]) {
        filters[f] = form?.[f];
      }
    });

    if (assignmentLineOfBusinessItems?.lob) {
      filters.lob = `${assignmentLineOfBusinessItems.lob}`;
    }

    if (form?.receiptStartDate && form?.receiptEndDate) {
      const startDate = dayjs(form?.receiptStartDate).startOf('day').toISOString();

      const endDate = dayjs(form?.receiptEndDate).startOf('day').toISOString();
      filters.endDate = `[\"${`${dayjs.utc(startDate).format('YYYY-MM-DD')}T00:00:00.000Z`}\", \"${`${dayjs.utc(endDate).format('YYYY-MM-DD')}T00:00:00.000Z`}\"]`;
    }

    if (form?.enteredStartDate && form?.enteredEndDate) {
      // Set the start date to the beginning of the day
      const startDate = dayjs(form?.enteredStartDate).startOf('day').toISOString();
      // Set the end date to the end of the day
      const endDate = dayjs(form?.enteredEndDate).endOf('day').toISOString();
      filters.createdAt = `[\"${dayjs.utc(startDate).format()}\", \"${dayjs.utc(endDate).format()}\"]`;
    }

    if (form?.payWeekEndingStart && form?.payWeekEndingEnd) {
      const payWeekEndingStart = dayjs(form?.payWeekEndingStart).startOf('day').toISOString();
      const payWeekEndingEnd = dayjs(form?.payWeekEndingEnd).endOf('day').toISOString();
      filters.payWeekEnding = `[\"${dayjs(payWeekEndingStart).format().split('T')[0]}\", \"${dayjs(payWeekEndingEnd).format().split('T')[0]}\"]`;
    }

    // CP-847: see buildStatementDateRange - this field needs its own encoding, not the
    // createdAt one. Verified against staging data.
    const statementDate = buildStatementDateRange(form?.statementStartDate, form?.statementEndDate);
    if (statementDate) {
      filters.statementDate = statementDate;
    }

    if (form?.isReceiptAttached === 'attached') {
      filters.documentName = `{ \"attributeExists\": ${true} }`;
    }

    if (form?.isReceiptAttached === 'not-attached') {
      filters.documentName = `{ \"attributeExists\": ${false} }`;
    }

    return filters;
  };

  const getFilteredExpenses = async token => {
    const filters = formatFilters();

    const newExp = await getExpensesFiltered(filters, token);

    // CP-859: a null payload is a failure, not an empty result set. AppSync returns
    // `data.listExpenseSearch: null` with NO errors[] entry whenever the resolver returns
    // nothing - which is precisely what the pre-CP-859 Lambda does on any exception, since
    // `return context.fail` serialises to null. Without this the portal would keep rendering a
    // backend failure as "0 results" on every environment where the Lambda has not been
    // deployed yet, and the error handling below would look like it worked while doing nothing.
    // An genuinely empty search returns { list: [], lastEvaluatedKey: null }, not null.
    if (newExp === null || newExp === undefined) {
      throw new Error('listExpenseSearch returned no payload');
    }

    const formattedExpenses = formatExpenses(newExp?.list);

    return {
      list: formattedExpenses,
      tokenResult: newExp?.lastEvaluatedKey,
    };
  };

  let currentCities = [];
  let prevToken = '';

  const getCitiesBatch = async (nextToken = null) => {
    setIsLoadingCities(true);
    if (prevToken !== nextToken) {
      prevToken = nextToken;
      const aData = await getCitiesFiltered({
        state: form?.expenseItemHotelStateId,
        token: nextToken,
      });
      const token = aData?.nextToken;

      if (aData?.items) {
        const items = aData?.items;
        currentCities = currentCities.concat(items);
      }
      if (token) {
        getCitiesBatch(token);
      } else {
        setField([
          {
            fieldName: 'expenseItemHotelCityId',
            fieldValue: '',
          },
        ]);
      }
      setSelectedCities({ selectedCities, cities: currentCities });
    }
    setIsLoadingCities(false);
  };

  const handleStateChange = async () => {
    setIsLoadingCities(true);
    await getCitiesBatch();
    setIsLoadingCities(false);
  };

  // CP-859: drains every page of the search, painting each batch as it arrives.
  //
  // This replaces a recursive getBatch that accumulated into plain `let`s in the component body
  // (reinitialised on every render, and sharing `prevToken` with getCitiesBatch) and only called
  // setExpenses once the very last page had returned. On the ticket's stage search that was 29
  // sequential round trips with a spinner and an empty table for 19.8 seconds.
  //
  // ResultsTable used to kick the drain off from its own mount effect, which also meant page 1
  // was fetched twice - once here, once there - and the first result thrown away. The drain lives
  // in one place now.
  const handleSearch = async () => {
    if (!fieldsAreValid()) {
      return;
    }

    // abandon any drain still in flight, so its pages cannot append into this one's results
    cancel();
    const searchId = activeSearchRef.current + 1;
    activeSearchRef.current = searchId;
    const ownsScreen = () => searchId === activeSearchRef.current;

    setSearchError(null);
    setResultsIncomplete(false);
    setExpenses([]);
    setShowResults(true);
    setLoadingResults(true);

    let token = null;
    let painted = 0;
    let pages = 0;
    let truncated = false;
    // The previous recursive drain guarded on `prevToken !== lastEvaluatedKey`, so a backend that
    // echoed a key simply stopped. A bare `while (token)` has no such floor: a repeated key would
    // loop forever, hammering AppSync from the browser. Both guards below restore that floor.
    const seenKeys = new Set();

    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const { aborted, data } = await runQuery(() => getFilteredExpenses(token));
        if (aborted) {
          // A newer search (or Cancel) owns the results now - leave its state alone.
          return;
        }

        if (data.list.length) {
          painted += data.list.length;
          setExpenses(prev => [...prev, ...data.list]);
        }

        pages += 1;
        token = data.tokenResult;

        if (token && (seenKeys.has(token) || pages >= MAX_SEARCH_PAGES)) {
          // Stop, but never present a capped result set as a complete one.
          truncated = true;
          token = null;
        }
        if (token) {
          seenKeys.add(token);
        }
      } while (token);

      if (truncated) {
        setResultsIncomplete(true);
        setSearchError(INCOMPLETE_SEARCH_MESSAGE);
      }
    } catch (e) {
      console.log(e);
      // A drain the user has already cancelled or replaced must stay silent - otherwise its late
      // failure raises an error over whatever is on screen now.
      if (ownsScreen()) {
        setResultsIncomplete(true);
        // Distinguish "nothing to show" from "these results are incomplete" - with incremental
        // painting the second case is now the common one, and silently truncating is the defect
        // this ticket set out to fix.
        setSearchError(painted ? INCOMPLETE_SEARCH_MESSAGE : 'Your search could not be completed. Please try again.');
      }
    } finally {
      if (ownsScreen()) {
        setLoadingResults(false);
      }
    }
  };

  const fileName = `expense-tracker-search-expenses-${newDateFormatted.toString()}.csv`;

  // CP-859: a complete result set - the drain finished and nothing failed or was capped.
  const canExport = !loadingResults && !resultsIncomplete && expenses.length > 0;

  // CP-859: this was called inline in the JSX, so it re-ran on every render. It is a nested loop
  // over expenses x expense items building a 35-column row each.
  //
  // Memoising alone is not enough now that rows append per page: `expenses` changes on every
  // batch, so the memo would rebuild the whole CSV once per page over a growing array - quadratic
  // across the drain - and throw each intermediate result away unread, since the export is not
  // offered until the drain finishes. Keyed on canExport as well, it is built exactly once.
  const csvData = useMemo(() => (canExport ? getSearchExpensesCsvData(expenses) : []), [canExport, expenses]);

  let exportUnavailableReason = 'There are no results to export.';
  if (loadingResults) {
    exportUnavailableReason = 'Still loading results - the export will be available once the search finishes.';
  } else if (resultsIncomplete) {
    exportUnavailableReason = 'This search did not finish, so its results cannot be exported.';
  }

  useEffect(() => {
    if (form?.expenseItemHotelStateId) {
      handleStateChange();
    }
  }, [form?.expenseItemHotelStateId]);

  useEffect(() => {
    dispatch(reset());
    setErrors([]);
    // CP-859: searchError is not form state, so the reset above does not touch it. Without this a
    // failed search on the MDS route carries its modal over to DMS, on top of a fresh form.
    setSearchError(null);
    setResultsIncomplete(false);
    cancel();
    activeSearchRef.current += 1;
  }, [location]);

  return (
    <>
      <SubHeader
        text="Expenses"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Expenses?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle={assignmentLineOfBusinessItems.text}
          middleTitle={showResults && 'Search Expenses'}
          midItemAction={() => {
            setShowResults(false);
            dispatch(reset());
          }}
          rightTitle={showResults ? 'Search Results' : 'Search Expenses'}
          route={assignmentLineOfBusinessItems.to}
          color={assignmentLineOfBusinessItems.lob === 'DMS' && '#ffff'}
        />
        {/* CP-859: this outer ternary is structural - its else branch is the pre-search control
            row, NOT "no export icon" - so it stays keyed on showResults alone. The export gate
            lives inside the results branch.

            The export is live only once the drain has finished cleanly. Results now paint while
            paging is still in progress, so otherwise a user could export a file that silently
            contains part of the result set, and unlike a truncated screen a truncated CSV leaves
            the building and gets acted on. `csvData` being memoised does not gate anything.

            When it is not available the icon still renders, inert, so the layout never shifts. */}
        {showResults ? (
          canExport ? (
            <CSVLink filename={fileName} data={csvData}>
              <ImageColorChanger
                src={Excel}
                style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
                alt="export-to-excel"
              />
            </CSVLink>
          ) : (
            <span className="export-unavailable" title={exportUnavailableReason}>
              <ImageColorChanger src={Excel} style={{ padding: '20px 30px 0 0 ' }} alt="export-to-excel" />
            </span>
          )
        ) : (
          <div style={{ marginRight: 35, marginTop: 30 }}>
            <Button
              variant="primary"
              style={{
                fontSize: '14px',
                minWidth: '115px',
                padding: '16px',
                textAlign: 'center',
                marginRight: '8px',
              }}
              onPress={() => handleSearch()}
              text="Search"
            />
            <Button
              variant="outline"
              style={{
                fontSize: '14px',
                minWidth: '115px',
                padding: '16px',
                textAlign: 'center',
                marginRight: '8px',
              }}
              onPress={() => setShowAdvancedSearch(sas => !sas)}
              text={showAdvancedSearch ? 'Simple' : 'Advanced'}
            />
            <Button
              variant="neutral"
              style={{
                padding: '14px',
                fontSize: '14px',
                textAlign: 'center',
                maxWidth: '116px',
              }}
              onPress={() => {
                dispatch(reset());
                setErrors([]);
              }}
              text="Reset Filters"
            />
          </div>
        )}
      </div>

      {/* CP-859: a failed or timed-out page used to be indistinguishable from an empty result
          set. Rendered outside the showResults branch so a total failure still surfaces. */}
      {searchError && <ErrorModal dismiss={() => setSearchError(null)} error={searchError} />}

      {showResults ? (
        <>
          {/* CP-859: data was `!loadingResults ? expenses : []`, which blanked the table for the
              whole drain - so appending per page would not have been visible at all. */}
          <ResultsTable data={expenses} loading={loadingResults} to={assignmentLineOfBusinessItems.to} />
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}>
            <Button
              variant="secondary"
              style={{
                padding: 12,
                marginTop: '20px',
                minWidth: '120px',
              }}
              onPress={() => {
                // CP-859: abandon an in-flight drain rather than letting it keep appending, and
                // take ownership of the screen so its late resolve or rejection stays silent.
                cancel();
                activeSearchRef.current += 1;
                setExpenses([]);
                setShowResults(false);
                setSearchError(null);
                setResultsIncomplete(false);
                setLoadingResults(false);
                dispatch(reset());
              }}
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <ExpensesWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <div style={{ padding: '0 20px 20px 20px' }}>
            {/* CP-859: SearchExpensesForm destructures a fixed prop list with no rest param, so
                thirteen of the props passed here were unreachable inside it - costOptions,
                setErrors, getCitiesFiltered, handleSearch, handleStateChange, setIsLoadingCities,
                isReceiptAttached, isLoadingStates, setExpenses, setForm, setIsReceiptAttached,
                setLoadingResults and setShowResults. Removed. */}
            <SearchExpensesForm
              airlines={airlines}
              attachedOptions={attachedOptions}
              branches={branches}
              clients={clients}
              carRentals={carRentals}
              drivers={drivers}
              errors={errors}
              form={form}
              hotels={hotels}
              isLoadingAirlines={isLoadingAirlines}
              isLoadingBranches={isLoadingBranches}
              isLoadingCarRentals={isLoadingCarRentals}
              isLoadingCities={isLoadingCities}
              isLoadingCustomers={isLoadingCustomers}
              isLoadingDrivers={isLoadingDrivers}
              isLoadingHotels={isLoadingHotels}
              setField={setField}
              selectedCities={selectedCities}
              showAdvancedSearch={showAdvancedSearch}
              states={states}
              statuses={statuses}
            />
            </div>
          }
          windowTitle="Search"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default SearchExpenses;
